"use client";
import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Search, ChevronRight, Quote, X } from "lucide-react";
// import { useBooking } from "../context/BookingContext";

// Case study background images
import orgImg from "@/public/images/case-study/org.png";
import simpleImg from "@/public/images/services/custom.png";
import shirtbayImg from "@/public/images/services/custom.png";
import nujoomImg from "@/public/images/services/custom.png";
import sellerImg from "@/public/images/services/custom.png";
import cherryImg from "@/public/images/services/custom.png";
import Image from "next/image";

import { caseStudies } from "@/app/data/casestudies";
import Link from "next/link";

import BookingModal from "../modal/booking-modal";

// Noise overlay for deep dive section
const NoiseOverlay = () => (
    <div
        className="absolute inset-0 opacity-[0.12] mix-blend-overlay pointer-events-none"
        style={{
            backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='180' height='180' filter='url(%23n)' opacity='.35'/%3E%3C/svg%3E\")",
        }}
    />
);


// Liquid Glass Case Study Tile (Gradient-based)
const CaseStudyTile = ({ study, design, onClick, isLarge = false }: any) => {
    const { baseGradient, blobs = [] } = design;

    return (
        <motion.button
            onClick={onClick}
            whileHover={{ y: -6, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className={[
                "group relative w-full h-full text-left",
                "rounded-[22px] overflow-hidden",
                "shadow-[0_24px_70px_rgba(10,20,40,0.18)]",
                "hover:shadow-[0_28px_80px_rgba(10,20,40,0.24)]",
                "transition-shadow duration-300",
            ].join(" ")}
        >
            {/* Base Gradient Background */}
            <div
                className="absolute inset-0"
                style={{ background: baseGradient }}
            />

            {/* Animated Liquid Blob Layers */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {blobs.map((blob: any, i: number) =>
                    blob.shape === "ringSoft" ? (
                        <div
                            key={i}
                            className="animated-ring"
                            style={{
                                width: blob.sizePx,
                                height: blob.sizePx,
                                left: blob.x,
                                top: blob.y,
                                transform: "translate(-50%, -50%)",
                                border: `2px solid ${blob.stroke}`,
                                filter: `blur(${blob.blurPx}px)`,
                                animationDelay: `${blob.delay || 0}s`,
                            }}
                        />
                    ) : (
                        <div
                            key={i}
                            className="animated-blob"
                            style={{
                                width: blob.sizePx,
                                height: blob.sizePx,
                                left: blob.x,
                                top: blob.y,
                                transform: "translate(-50%, -50%)",
                                background: blob.fill,
                                filter: `blur(${blob.blurPx}px)`,
                                mixBlendMode: blob.blendMode || "soft-light",
                                animationDelay: `${blob.delay || 0}s`,
                            }}
                        />
                    )
                )}
            </div>

            {/* Noise texture */}
            <div className="tile-noise" />

            {/* Vignette */}
            <div className="tile-vignette" />

            {/* Hover sheen */}
            <div className="tile-sheen" />

            {/* Content Container - Centered title + bottom text */}
            <div className="absolute inset-0 flex flex-col justify-between p-3 sm:p-4 md:p-5 z-10">
                {/* Center Area - Title */}
                <div className="flex-1 flex items-center justify-center">
                    <h3
                        className={`text-start text-white px-2 sm:px-4 ${isLarge ? 'text-[22px] sm:text-[26px] md:text-[26px]' : 'text-[18px] sm:text-[20px] md:text-[24px]'}`}
                        style={{
                            fontWeight: 750,
                            letterSpacing: -0.3,
                            textShadow: '0 2px 12px rgba(0,0,0,0.3)',
                            lineHeight: 1.2,
                        }}
                    >
                     {study.tagline}
                    </h3>
                </div>

                {/* Bottom Text Block */}
                <div>
                    <span
                        className="block mb-1 sm:mb-1.5 text-[10px] sm:text-[11px] md:text-[12px] px-2 sm:px-4 "
                        style={{
                            fontWeight: 600,
                            color: 'rgba(255,255,255,0.75)',
                            textShadow: '0 1px 4px rgba(0,0,0,0.3)',
                        }}
                    >
                       <span >{study.domain} </span> 
                       {/* <span className="cursor-pointer">view details</span> */}
                         {/* {study.duration} */}
                    </span>
                    {/* <p
                        className="text-[12px] sm:text-[13px] md:text-[14px] line-clamp-2"
                        style={{
                            fontWeight: 600,
                            color: 'rgba(255,255,255,0.95)',
                            lineHeight: 1.45,
                            textShadow: '0 1px 4px rgba(0,0,0,0.25)',
                        }}
                    >
                        {study.tagline}
                    </p> */}
                </div>
            </div>
        </motion.button>
    );
};

const EmptyTile = () => (
    <div className="w-full h-full rounded-[22px] bg-tile-empty relative overflow-hidden">
        <div className="tile-noise" style={{ opacity: 0.06 }} />
    </div>
);

const tileDesigns = {
    orgupdate: {
        baseGradient: "linear-gradient(135deg, #6E73F5 0%, #5B63D8 42%, #2E2B6E 100%)",
        blobs: [
            { shape: "blob", x: "20%", y: "30%", sizePx: 280, fill: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.35), rgba(255,255,255,0) 65%)", blurPx: 20, blendMode: "soft-light", delay: 0 },
            { shape: "blob", x: "75%", y: "60%", sizePx: 240, fill: "radial-gradient(circle at 40% 40%, rgba(140,180,255,0.45), rgba(140,180,255,0) 65%)", blurPx: 24, blendMode: "screen", delay: -4 },
            { shape: "blob", x: "50%", y: "20%", sizePx: 200, fill: "radial-gradient(circle at 50% 50%, rgba(255,200,255,0.25), rgba(255,200,255,0) 60%)", blurPx: 28, blendMode: "screen", delay: -7 },
        ],
    },
    "simple-numbers": {
        baseGradient: "linear-gradient(135deg, #0B6C86 0%, #0B5C74 40%, #063B52 100%)",
        blobs: [
            { shape: "blob", x: "25%", y: "35%", sizePx: 380, fill: "radial-gradient(circle at 35% 35%, rgba(100,240,230,0.35), rgba(100,240,230,0) 65%)", blurPx: 22, blendMode: "screen", delay: 0 },
            { shape: "blob", x: "70%", y: "60%", sizePx: 300, fill: "radial-gradient(circle at 40% 40%, rgba(255,200,150,0.30), rgba(255,200,150,0) 65%)", blurPx: 26, blendMode: "soft-light", delay: -5 },
            { shape: "ringSoft", x: "85%", y: "45%", sizePx: 420, stroke: "rgba(255,255,255,0.18)", blurPx: 6, delay: -2 },
        ],
    },
    shirtbay: {
        baseGradient: "linear-gradient(135deg, #7B1D2E 0%, #5C1D2F 42%, #2C1A3A 100%)",
        blobs: [
            { shape: "blob", x: "25%", y: "35%", sizePx: 260, fill: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.30), rgba(255,255,255,0) 65%)", blurPx: 20, blendMode: "soft-light", delay: 0 },
            { shape: "blob", x: "75%", y: "65%", sizePx: 240, fill: "radial-gradient(circle at 40% 40%, rgba(255,160,120,0.35), rgba(255,160,120,0) 65%)", blurPx: 24, blendMode: "screen", delay: -3 },
            { shape: "blob", x: "55%", y: "25%", sizePx: 180, fill: "radial-gradient(circle at 50% 50%, rgba(255,100,150,0.25), rgba(255,100,150,0) 60%)", blurPx: 26, blendMode: "screen", delay: -6 },
        ],
    },
    "nujoom-ai": {
        baseGradient: "linear-gradient(135deg, #2B3A9A 0%, #2A2F7C 44%, #141A3A 100%)",
        blobs: [
            { shape: "blob", x: "22%", y: "30%", sizePx: 280, fill: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.30), rgba(255,255,255,0) 65%)", blurPx: 18, blendMode: "soft-light", delay: 0 },
            { shape: "blob", x: "70%", y: "55%", sizePx: 240, fill: "radial-gradient(circle at 40% 40%, rgba(150,140,255,0.35), rgba(150,140,255,0) 65%)", blurPx: 22, blendMode: "screen", delay: -4 },
            { shape: "ringSoft", x: "80%", y: "60%", sizePx: 360, stroke: "rgba(255,255,255,0.18)", blurPx: 6, delay: -2 },
        ],
    },
    "seller-metrics": {
        baseGradient: "linear-gradient(135deg, #0D5A46 0%, #0B453A 45%, #062821 100%)",
        blobs: [
            { shape: "blob", x: "28%", y: "38%", sizePx: 320, fill: "radial-gradient(circle at 30% 30%, rgba(100,255,220,0.30), rgba(100,255,220,0) 65%)", blurPx: 22, blendMode: "screen", delay: 0 },
            { shape: "blob", x: "72%", y: "58%", sizePx: 260, fill: "radial-gradient(circle at 40% 40%, rgba(255,200,160,0.25), rgba(255,200,160,0) 65%)", blurPx: 26, blendMode: "soft-light", delay: -5 },
            { shape: "blob", x: "50%", y: "22%", sizePx: 200, fill: "radial-gradient(circle at 50% 50%, rgba(150,255,200,0.20), rgba(150,255,200,0) 60%)", blurPx: 28, blendMode: "screen", delay: -8 },
        ],
    },
    cherrysprites: {
        baseGradient: "linear-gradient(135deg, #6A2F66 0%, #4B2B53 48%, #2A1C33 100%)",
        blobs: [
            { shape: "blob", x: "22%", y: "32%", sizePx: 280, fill: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.30), rgba(255,255,255,0) 65%)", blurPx: 20, blendMode: "soft-light", delay: 0 },
            { shape: "blob", x: "75%", y: "62%", sizePx: 240, fill: "radial-gradient(circle at 40% 40%, rgba(255,160,220,0.35), rgba(255,160,220,0) 65%)", blurPx: 24, blendMode: "screen", delay: -4 },
            { shape: "blob", x: "52%", y: "20%", sizePx: 180, fill: "radial-gradient(circle at 50% 50%, rgba(200,140,255,0.25), rgba(200,140,255,0) 60%)", blurPx: 26, blendMode: "screen", delay: -7 },
        ],
    },
};

const caseStudyImages = {
    'orgupdate': orgImg,
    "simple-numbers": simpleImg,
    shirtbay: shirtbayImg,
    "nujoom-ai": nujoomImg,
    "seller-metrics": sellerImg,
    cherrysprites: cherryImg,
};

const CaseS = () => {
    //   const { openBookingModal, openContactModal } = useBooking();
    const [activeFilter, setActiveFilter] = useState("All");
    const [searchQuery, setSearchQuery] = useState("");
    const [activeStudy, setActiveStudy] = useState<string | null>(null);
    const deepDiveRef = useRef<HTMLDivElement | null>(null);
    const filters = ["All", "Data", "AI", "Analytics", "Commerce", "Collaboration"];
    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

    // Premium cover themes by category (using CSS classes from index.css)
    const auroraThemes = {
        // Data: Lavender
        Data: {
            cover: "bg-tile-lavender",
            gradient: "from-indigo-500 via-blue-400 to-cyan-400",
            gradientLight: "from-indigo-100 via-blue-50 to-cyan-50",
            pill: "bg-white/20 text-white ring-1 ring-white/25",
            pillLight: "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200/60",
            accent: "text-indigo-600",
            border: "border-indigo-100 hover:border-indigo-200",
        },
        // Analytics: Teal-Warm
        Analytics: {
            cover: "bg-tile-teal-warm",
            gradient: "from-sky-500 via-teal-400 to-emerald-400",
            gradientLight: "from-sky-100 via-teal-50 to-emerald-50",
            pill: "bg-white/20 text-white ring-1 ring-white/25",
            pillLight: "bg-sky-50 text-sky-700 ring-1 ring-sky-200/60",
            accent: "text-teal-600",
            border: "border-sky-100 hover:border-sky-200",
        },
        // Commerce: Warm Energy
        Commerce: {
            cover: "bg-tile-warm-energy",
            gradient: "from-orange-400 via-rose-400 to-pink-500",
            gradientLight: "from-orange-50 via-rose-50 to-pink-50",
            pill: "bg-white/20 text-white ring-1 ring-white/25",
            pillLight: "bg-rose-50 text-rose-700 ring-1 ring-rose-200/60",
            accent: "text-rose-600",
            border: "border-rose-100 hover:border-rose-200",
        },
        // AI: Deep Blue
        AI: {
            cover: "bg-tile-deep-blue",
            gradient: "from-violet-500 via-purple-500 to-fuchsia-500",
            gradientLight: "from-violet-100 via-purple-50 to-fuchsia-50",
            pill: "bg-white/20 text-white ring-1 ring-white/25",
            pillLight: "bg-violet-50 text-violet-700 ring-1 ring-violet-200/60",
            accent: "text-violet-600",
            border: "border-violet-100 hover:border-violet-200",
        },
        // Collaboration: Rose Soft
        Collaboration: {
            cover: "bg-tile-rose-soft",
            gradient: "from-emerald-500 via-teal-500 to-cyan-500",
            gradientLight: "from-emerald-100 via-teal-50 to-cyan-50",
            pill: "bg-white/20 text-white ring-1 ring-white/25",
            pillLight: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60",
            accent: "text-sky-600",
            border: "border-emerald-100 hover:border-emerald-200",
        },
    };



    // Filter and search logic
    const filteredStudies = caseStudies.filter(study => {
        const matchesFilter = activeFilter === "All" || study.domain === activeFilter;
        const matchesSearch = searchQuery === "" ||
            study.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            study.tagline.toLowerCase().includes(searchQuery.toLowerCase()) ||
            study.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
            study.tech_stack.some(tech => tech.toLowerCase().includes(searchQuery.toLowerCase())) ||
            study.focus_tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
        return matchesFilter && matchesSearch;
    });

    // Handle deep dive drawer
    const handleOpenDeepDive = (studyId: string) => {
        setActiveStudy(studyId);
    };

    const handleCloseDeepDive = () => {
        setActiveStudy(null);
    };

    const handleNextStudy = () => {
        const currentIndex = filteredStudies.findIndex(s => s.id === activeStudy);
        const nextIndex = (currentIndex + 1) % filteredStudies.length;
        setActiveStudy(filteredStudies[nextIndex].id);
    };

    // Scroll to deep dive when opened
    useEffect(() => {
        if (activeStudy && deepDiveRef.current) {
            setTimeout(() => {
                deepDiveRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 100);
        }
    }, [activeStudy]);

    const activeStudyData = caseStudies.find(s => s.id === activeStudy);

    const customEasing: [number, number, number, number] = [
        0.22,
        1,
        0.36,
        1,
    ];

    // Get tile design by study ID
    const getTileDesign = (studyId: string) => {
        return (
            tileDesigns[studyId as keyof typeof tileDesigns] ||
            tileDesigns.orgupdate
        );
    };

    return (
        <>
           <div className="bg-white min-h-screen">
            {/* Header Block - ElevenLabs Style */}
            <section className="pt-20 sm:pt-24 md:pt-28 pb-8 sm:pb-12 bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 sm:gap-6">
                        {/* Left: Title */}
                        <div>
                            <span className="text-[11px] sm:text-[12px] font-semibold uppercase tracking-wider text-zinc-400 mb-2 sm:mb-3 block">
                                Case Studies
                            </span>
                            <h1 className="text-[28px] sm:text-[36px] md:text-[48px] leading-[1.1] font-semibold tracking-tight text-zinc-900">
                                Real Software Projects. Real Business Outcomes.
                            </h1>
                            <p className="mt-3 sm:mt-4 text-[14px] sm:text-[15px] leading-6 text-zinc-500 max-w-2xl">
                                Explore how ZySoftec helps startups and growing businesses build SaaS platforms, AI-powered products, custom software systems, and digital experiences that drive measurable growth, efficiency, and long-term scalability. </p>
                        </div>

                        {/* Right: CTA */}
                        <button
                         onClick={() =>
                            setIsBookingModalOpen(true)
                          }
                            //   onClick={openBookingModal}
                            className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 rounded-full bg-zinc-900 text-white text-[13px] sm:text-[14px] font-medium hover:bg-zinc-800 transition-colors whitespace-nowrap w-full sm:w-auto"
                        >
                            Book a call
                            <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </section>

            {/* Platform Toggle - ElevenLabs Style Pill */}
            <section className="pb-6 sm:pb-10 bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
                        <div className="inline-flex rounded-full border border-zinc-200 bg-zinc-50 p-1 min-w-max">
                            {filters.map((filter) => (
                                <button
                                    key={filter}
                                    onClick={() => setActiveFilter(filter)}
                                    className={`px-3 sm:px-4 py-1.5 sm:py-2 text-[12px] sm:text-[13px] rounded-full transition-all whitespace-nowrap ${activeFilter === filter
                                        ? "bg-white shadow-sm text-zinc-900 font-medium"
                                        : "text-zinc-500 hover:text-zinc-800"
                                        }`}
                                >
                                    {filter}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Grid - Liquid Glass Mosaic (Bento Layout) */}
            <section className="pb-12 sm:pb-16 md:pb-20 bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    {filteredStudies.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-[18px]">
                            {/* Row 1: OrgUpdate (2 cols) + Simple Numbers (2 cols, spans 2 rows) */}
                            {filteredStudies[0] && (
                                <div className="sm:col-span-2 lg:col-span-2 h-[180px] sm:h-[190px]">
                                    {/* <CaseStudyTile
                                        study={filteredStudies[0]}
                                        design={getTileDesign(filteredStudies[0].id)}
                                        onClick={() => handleOpenDeepDive(filteredStudies[0].id)}
                                    /> */}
                                    <Link href={`/case-studies/${filteredStudies[0].id}`}>
                                        <CaseStudyTile
                                            study={filteredStudies[0]}
                                            design={getTileDesign(filteredStudies[0].id)}
                                        />
                                    </Link>
                                </div>
                            )}

                            {filteredStudies[1] && (
                                <div className="sm:col-span-2 lg:col-span-2 lg:row-span-2 h-[180px] sm:h-[190px] lg:h-[398px]">
                                   <Link href={`/case-studies/${filteredStudies[1].id}`}>
                                        <CaseStudyTile
                                            study={filteredStudies[1]}
                                            design={getTileDesign(filteredStudies[1].id)}
                                        />
                                    </Link>
                                </div>
                            )}

                            {/* Row 2: Personalized (1 col) + Nujoom AI (1 col) */}
                            {filteredStudies[2] && (
                                <div className="h-[180px] sm:h-[190px]">
                                    <Link href={`/case-studies/${filteredStudies[2].id}`}>
                                        <CaseStudyTile
                                            study={filteredStudies[2]}
                                            design={getTileDesign(filteredStudies[2].id)}
                                        />
                                    </Link>
                                </div>
                            )}

                            {filteredStudies[3] && (
                                <div className="h-[180px] sm:h-[190px]">
                                    <Link href={`/case-studies/${filteredStudies[3].id}`}>
                                        <CaseStudyTile
                                            study={filteredStudies[3]}
                                            design={getTileDesign(filteredStudies[3].id)}
                                        />
                                    </Link>
                                </div>
                            )}

                            {/* Row 3: Seller Metrics (2 cols) + Cherrysprites (1 col) + Empty (1 col) */}
                            {filteredStudies[4] && (
                                <div className="sm:col-span-2 h-[180px] sm:h-[190px]">
                                    <Link href={`/case-studies/${filteredStudies[4].id}`}>
                                        <CaseStudyTile
                                            study={filteredStudies[4]}
                                            design={getTileDesign(filteredStudies[4].id)}
                                        />
                                    </Link>
                                </div>
                            )}

                            {filteredStudies[5] && (
                                <div className="h-[180px] sm:h-[190px]">
                                     <Link href={`/case-studies/${filteredStudies[5].id}`}>
                                        <CaseStudyTile
                                            study={filteredStudies[5]}
                                            design={getTileDesign(filteredStudies[5].id)}
                                        />
                                    </Link>
                                </div>
                            )}

                            {/* Empty filler tile */}
                            <div className="hidden lg:block h-[190px]">
                                <EmptyTile />
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-12 sm:py-16">
                            <p className="text-[14px] sm:text-[15px] text-zinc-500">
                                No case studies match your filter. Try selecting a different category.
                            </p>
                        </div>
                    )}
                </div>
            </section>

            {/* Deep Dive - Inline Section (below grid) */}
            <AnimatePresence mode="wait">
                {activeStudy && activeStudyData && (
                    <motion.section
                        ref={deepDiveRef}
                        key={activeStudy}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="py-6 sm:py-8 md:py-12 bg-[#FAFAFA]"
                    >
                        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
                            {(() => {
                                // const theme = auroraThemes[activeStudyData.domain] || auroraTheme÷s.Data;
                                const theme = auroraThemes[activeStudyData.domain as keyof typeof auroraThemes] || auroraThemes.Data;
                                const headerImage = caseStudyImages[activeStudyData.id as keyof typeof caseStudyImages];
                                // const headerImage = caseStudyImages[activeStudyData.id];

                                return (
                                    <div className="bg-white rounded-xl sm:rounded-2xl shadow-[0_10px_40px_rgba(2,6,23,0.08)] overflow-hidden">
                                        {/* Header with background image + close button */}
                                        <div className="relative h-36 sm:h-44 md:h-56 overflow-hidden">
                                            {/* Background Image */}
                                            <div
                                                className="absolute inset-0 bg-cover bg-center"
                                                style={{ backgroundImage: `url(${activeStudyData?.image})` }}
                                            />
                                            {/* Dark overlay for text readability */}
                                            <div
                                                className="absolute inset-0"
                                                style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.65) 100%)' }}
                                            />
                                            {/* Subtle glow blobs */}
                                            <div className="absolute -top-10 -left-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
                                            <div className="absolute -bottom-10 -right-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
                                            <NoiseOverlay />

                                            {/* Header content */}
                                            <div className="relative h-full mx-auto max-w-4xl px-4 sm:px-6 md:px-8 flex items-end pb-4 sm:pb-6">
                                                <div className="flex-1 min-w-0">
                                                    <span className="inline-flex items-center rounded-full bg-white/20 backdrop-blur-sm px-2 sm:px-3 py-1 text-[10px] sm:text-[11px] font-semibold tracking-wide uppercase text-white mb-1.5 sm:mb-2">
                                                        {activeStudyData.domain} · Case Study
                                                    </span>
                                                    <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-white leading-tight truncate" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.4)' }}>
                                                        {activeStudyData.name} 
                                                    </h2>
                                                </div>

                                                {/* Close + Nav buttons */}
                                                <div className="flex items-center gap-1.5 sm:gap-2 ml-2 flex-shrink-0">
                                                    <button
                                                        onClick={handleNextStudy}
                                                        className="hidden sm:flex items-center gap-1 px-3 py-2 rounded-full bg-white/20 backdrop-blur-sm text-[12px] font-medium text-white hover:bg-white/30 transition-colors"
                                                    >
                                                        Next <ChevronRight className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={handleCloseDeepDive}
                                                        className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                                                    >
                                                        <X className="w-4 h-4 sm:w-5 sm:h-5" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Content - Single column, max-w-4xl */}
                                        <div className="mx-auto max-w-4xl px-4 sm:px-6 md:px-8 py-5 sm:py-6 md:py-8">
                                            {/* A) Overview */}
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.1 }}
                                                className="mb-5 sm:mb-6 md:mb-8"
                                            >
                                                <p className="text-[14px] sm:text-[16px] md:text-[17px] text-slate-800 font-medium leading-relaxed">
                                                    {activeStudyData.tagline}
                                                </p>
                                                <p className="mt-2 text-[13px] sm:text-[14px] text-slate-500 leading-relaxed ">
                                                    {activeStudyData.summary}
                                                </p>
                                            </motion.div>

                                            {/* B) Challenge - 3 bullets */}
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.15 }}
                                                className="mb-5 sm:mb-6 md:mb-8"
                                            >
                                                <h3 className="text-[11px] sm:text-[12px] uppercase tracking-wide text-slate-400 font-semibold mb-3 sm:mb-4">
                                                    Challenge
                                                </h3>
                                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                                                    {activeStudyData.what_needed.slice(0, 3).map((item, idx) => (
                                                        <div key={idx} className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg sm:rounded-xl bg-slate-50 border border-slate-100">
                                                            <span className={`w-5 h-5 sm:w-6 sm:h-6 rounded-md sm:rounded-lg ${theme.pillLight} text-[10px] sm:text-[11px] flex items-center justify-center flex-shrink-0 font-semibold`}>
                                                                {idx + 1}
                                                            </span>
                                                            <p className="text-[12px] sm:text-[13px] text-slate-600 leading-relaxed">{item}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </motion.div>

                                            {/* C) What We Built - 3 bullets */}
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.2 }}
                                                className="mb-5 sm:mb-6 md:mb-8"
                                            >
                                                <h3 className="text-[11px] sm:text-[12px] uppercase tracking-wide text-slate-400 font-semibold mb-3 sm:mb-4">
                                                    What We Built
                                                </h3>
                                                <Image src={activeStudyData?.image} alt="image" className="w-full h-full" />
                                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                                                    {activeStudyData.what_built_layers.slice(0, 3).map((layer, idx) => (
                                                        <div key={idx} className="p-3 sm:p-4 rounded-lg sm:rounded-xl bg-white border border-slate-200 shadow-sm">
                                                            <p className="text-[13px] sm:text-[14px] text-slate-800 font-semibold">{layer.layer}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </motion.div>

                                            {/* D) Results - 3 metrics */}
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.25 }}
                                                className="mb-5 sm:mb-6 md:mb-8"
                                            >
                                                <h3 className="text-[11px] sm:text-[12px] uppercase tracking-wide text-slate-400 font-semibold mb-3 sm:mb-4">
                                                    Results
                                                </h3>
                                                <div className={`p-4 sm:p-6 rounded-lg sm:rounded-xl bg-gradient-to-br ${theme.gradientLight} border border-slate-200`}>
                                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                                                        {activeStudyData.outcomes.slice(0, 3).map((outcome, idx) => {
                                                            const numMatch = outcome.match(/(\d+%?|~?\d+-?\d*%?)/);
                                                            return (
                                                                <div key={idx} className="text-center sm:text-left">
                                                                    {numMatch && (
                                                                        <p className={`text-xl sm:text-2xl md:text-3xl font-bold ${theme.accent} mb-1`}>
                                                                            {numMatch[0]}
                                                                        </p>
                                                                    )}
                                                                    <p className="text-[12px] sm:text-[13px] text-slate-600 leading-snug">
                                                                        {numMatch ? outcome.replace(numMatch[0], "").trim() : outcome}
                                                                    </p>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </motion.div>

                                            {/* Tech Stack - max 6 */}
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.3 }}
                                                className="mb-5 sm:mb-6 md:mb-8"
                                            >
                                                <h3 className="text-[11px] sm:text-[12px] uppercase tracking-wide text-slate-400 font-semibold mb-3 sm:mb-4">
                                                    Tech Stack
                                                </h3>
                                                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                                    {activeStudyData.tech_stack.slice(0, 6).map((tech, idx) => (
                                                        <span key={idx} className="text-[11px] sm:text-[12px] px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                                                            {tech}
                                                        </span>
                                                    ))}
                                                    {activeStudyData.tech_stack.length > 6 && (
                                                        <span className="text-[11px] sm:text-[12px] px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-slate-50 text-slate-400 font-medium">
                                                            +{activeStudyData.tech_stack.length - 6} more
                                                        </span>
                                                    )}
                                                </div>
                                            </motion.div>

                                            {/* Footer CTA */}
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.35 }}
                                                className="pt-4 sm:pt-6 border-t border-slate-200"
                                            >
                                                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                                                    <p className="text-[13px] sm:text-[14px] text-slate-500">
                                                        Interested in similar results?
                                                    </p>
                                                    <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
                                                        <button
                                                            onClick={handleNextStudy}
                                                            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 border border-slate-200 text-[12px] sm:text-[13px] font-medium text-slate-700 rounded-full hover:bg-slate-50 transition-all"
                                                        >
                                                            Next Study <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                                        </button>
                                                        <button
                                                             onClick={() =>
                                                                setIsBookingModalOpen(true)
                                                              }

                                                            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-slate-900 text-[12px] sm:text-[13px] font-semibold text-white rounded-full hover:bg-slate-800 transition-all"
                                                        >
                                                            Book a Call <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </motion.section>
                )}
            </AnimatePresence>

            {/* Closing Summary */}
            <section className="py-10 sm:py-12 md:py-16 bg-white border-t border-[#E5E7EB]">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.38, ease: "easeOut" }}
                        className="text-center max-w-3xl mx-auto"
                    >
                        <p className="text-[16px] sm:text-[18px] md:text-[24px] text-[#64748B] mb-2 sm:mb-3" style={{ fontWeight: 400, lineHeight: 1.5 }}>
                            Every project follows the same principle:
                        </p>
                        <p className="text-[16px] sm:text-[18px] md:text-[24px] text-[#0F172A] mb-5 sm:mb-6 md:mb-8" style={{ fontWeight: 500, lineHeight: 1.5 }}>
                            Build systems that scale with your business, not against it.
                        </p>

                        <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-2 sm:gap-4 md:gap-6 text-[12px] sm:text-[13px] md:text-[14px] text-[#64748B]" style={{ fontWeight: 400 }}>
                            <span>120+ successful launches</span>
                            <span className="hidden sm:inline text-[#E5E7EB]">|</span>
                            <span>Driving measurable business growth</span>
                            <span className="hidden sm:inline text-[#E5E7EB]">|</span>
                            <span>Long-term partnerships</span>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Global CTA */}
            <section
                className="py-12 sm:py-16 lg:py-20 relative overflow-hidden"
                style={{
                    background: `
            radial-gradient(500px 200px at 30% 0%, rgba(242,166,90,0.35), transparent 60%),
            radial-gradient(500px 200px at 70% 100%, rgba(122,111,240,0.35), transparent 60%),
            #0B0B0F
          `
                }}
            >
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="lg:flex lg:gap-12 lg:items-start">
                        {/* Left Column */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.32 }}
                            className="lg:w-[58%] mb-8 lg:mb-0"
                        >
                            <h2 className="text-2xl sm:text-[28px] md:text-[34px] lg:text-[40px] text-white mb-4" style={{ fontWeight: 500, lineHeight: 1.15, letterSpacing: "-0.02em" }}>
                                Ready to Build a System That Scales?
                            </h2>
                            <p className="text-base sm:text-[17px] text-white/70 mb-4 sm:mb-6" style={{ fontWeight: 400, lineHeight: 1.6 }}>
                                Whether you're starting from scratch or improving an existing product, we can help you build software that supports your growth.
                            </p>
                            <p className="text-sm sm:text-[15px] text-white/60" style={{ fontWeight: 400, lineHeight: 1.7 }}>
                                Every engagement starts with a conversation. We'll discuss your goals, constraints, and what success looks like for your project.
                            </p>
                        </motion.div>

                        {/* Right Column - CTA Panel */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.32, delay: 0.1 }}
                            className="lg:w-[42%]"
                        >
                            <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 sm:p-8">
                                <p className="text-[11px] sm:text-[12px] uppercase tracking-wide text-[#7A6FF0] mb-4 sm:mb-6 text-center" style={{ fontWeight: 500 }}>
                                    Next Step
                                </p>
                                <p className="text-sm sm:text-[14px] text-white/70 mb-4 sm:mb-6 text-center" style={{ fontWeight: 400, lineHeight: 1.6 }}>
                                    Schedule a free consultation to discuss your project requirements.
                                </p>

                                <motion.button
                                    //   onClick={openBookingModal}
                                    onClick={() =>
                                        setIsBookingModalOpen(true)
                                      }
                                    whileHover={{ y: -2 }}
                                    transition={{ duration: 0.16 }}
                                    className="w-full inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm sm:text-[15px] font-semibold text-[#0B0B0F] transition-all duration-200 hover:bg-white/90 hover:shadow-lg hover:shadow-white/10 mb-4"
                                >
                                    Book a Free Consultation
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </motion.button>

                                <div className="flex items-center justify-center gap-3 sm:gap-4">
                                    <button
                                        // onClick={openContactModal}
                                        className="text-sm sm:text-[14px] text-white/70 hover:text-white transition-colors"
                                        style={{ fontWeight: 500 }}
                                    >
                                        Contact Us
                                    </button>
                                    <span className="text-white/30">·</span>
                                    <a
                                        href="mailto:hello@zysoftec.com"
                                        className="text-sm sm:text-[14px] text-white/70 hover:text-white transition-colors"
                                        style={{ fontWeight: 500 }}
                                    >
                                        Email Us
                                    </a>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
         <BookingModal
        open={isBookingModalOpen}
        onClose={() =>
          setIsBookingModalOpen(false)
        }
      />
        </>
     
    );
};

export default CaseS;
